import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Col } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import styles from './styles.scss';
import Translation from '../../../../components/stateless/Translation';
import { formatCurrency } from '../../../../../src/shared/i18n';
import ThemedButton from '../../../../components/stateless/ThemedButton';
import { pushRoute } from '../../../../../modules/routing';
import intlPath from '../../../../../src/web/utils/intlPath';
import { reviewCost, clearMoveIn } from '../../../../../src/web/store/modules/movein';
import { clearReservation } from '../../../../../src/web/store/modules/reservations';
import { compose } from '../../../../../src/web/utils/react';

const propTypes = {
  facility: PropTypes.shape().isRequired,
  unitGroup: PropTypes.shape().isRequired,
  moveInPath: PropTypes.string.isRequired,
  toggleCreateLeadModal: PropTypes.func.isRequired,
};

const defaultProps = {};

const UnitListingRow = ({
  unitGroup,
  facility,
  moveInPath,
  completeReviewCost,
  dispatchClearReservation,
  dispatchClearMoveIn,
  toggleCreateLeadModal,
}) => {
  const intl = useIntl();
  const { locale } = intl;
  const {
    id: unitGroupId,
    width,
    length,
    height,
    area,
    categoryName,
    availableUnitsCount,
    discountedPrice,
    promoId,
    pricedWeekly,
    price,
    leadOnly,
  } = unitGroup;
  const {
    id: facilityId,
    currencySymbol,
    address: {
      state,
    },
    settings: {
      softwareProvider,
    },
  } = facility;
  const [available, setAvailability] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const isParking = /parking|vehicle/i.test(categoryName);
  const unitPrice = discountedPrice || price;

  const promo = !!promoId && (promoId === 0 ? false : (
    <Translation id={`widgets.unitstablev2.storageMart.unitPromo.${promoId}`}>
      {text => <span className={styles.promoAlert}><i className="fa fa-tag" aria-hidden="true" /> {text}</span>}
    </Translation>
  ));

  const handleReserveUnit = async () => {
    if (leadOnly) {
      toggleCreateLeadModal(unitGroupId);
    } else {
      setSubmitting(true);
      dispatchClearMoveIn();

      await completeReviewCost(unitGroupId, facilityId, null, softwareProvider)
        .then(() => {
          setSubmitting(false);
          dispatchClearReservation();
          pushRoute(`${intlPath(locale, moveInPath)}?unitGroupId=${unitGroupId}&facilityId=${facilityId}`);
        }).catch(() => {
          setSubmitting(false);
          setAvailability(false);
        });
    }
  };

  return (
    <div className={classNames(styles.unitGroupRow, 'row')}>
      <Col xs={6} lg={4}>
        <div className={styles.unitSizeContainer}>
          <span>{`${width}'`}</span>
          <small>
            <Translation
              id="widgets.unitsTablev2.storageMart.widthInitial"
              defaultMessage="w"
            />
          </small>
          {' '}
          <span className={styles.unitSizeX}>X</span>
          {' '}
          <span>{`${length}'`}</span>
          <small>
            <Translation
              id="widgets.unitsTablev2.storageMart.depthInitial"
              defaultMessage="d"
            />
          </small>
          {!isParking && (
            <>
              {' '}
              <span className={styles.unitSizeX}>X</span>
              {' '}
              <span>{`${height}'`}</span>
              <small>
                <Translation
                  id="widgets.unitsTablev2.storageMart.heightInitial"
                  defaultMessage="h"
                />
              </small>
            </>
          )}
          <div className={styles.unitSizeArea}>
            {area}
            {' '}
            <Translation
              id="widgets.unitsTablev2.storageMart.sqft"
              defaultMessage="sq ft"
            />
            {!isParking && (
              <>
                {' ('}
                {(area * height).toFixed(0)}
                {' '}
                <Translation
                  id="widgets.unitsTablev2.storageMart.cuft"
                  defaultMessage="cu ft"
                />
                {')'}
              </>
            )}
          </div>
        </div>
      </Col>
      <Col xs={6} lg={4}>
        <div className={styles.priceContainer}>
          {(availableUnitsCount === 0 || !available)
            ? (
              <div>
                <span className={styles.soldOut}>
                  <Translation id="widgets.unitsTablev2.storageMart.soldOut" defaultMessage="Sold Out" />
                </span>
              </div>
            )
            : (
              <>
                {availableUnitsCount < 5 && availableUnitsCount > 0 && available
                  && (
                  <div className={styles.inventoryAlert}>
                    <Translation
                      id="widgets.unitsTablev2.storageMart.occupancyCallOut"
                      defaultMessage="Only {count} left"
                      values={{ count: availableUnitsCount }}
                    />
                  </div>
                  )
                }
                {unitPrice !== null
                  && (
                  <>
                    <div className={styles.discountDescriptiveOnlineOnly}>
                      <span
                        className={styles.discountDescriptiveNumberOnlineOnly}
                      >
                        {formatCurrency({
                          price: unitPrice,
                          decimals: 2,
                          currencySymbol,
                          state,
                        })}
                      </span>
                      <sup>
                        {pricedWeekly
                          ? <Translation id="widgets.abbreviation.wk" defaultMessage="/wk" />
                          : <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
                        }
                      </sup>
                    </div>
                  </>
                  )
                }
                {promo}
              </>
            )
          }
        </div>
      </Col>
      <Col xs={12} lg={4}>
        <div className={styles.reserveButtonContainer}>
          {(availableUnitsCount === 0 || !available)
            ? (
              <>
                <div className={styles.callForAvailability}>
                  {available
                    ? (
                      <Translation
                        id="widgets.unitsTablev2.storageMart.callForAvailability"
                        defaultMessage="Call for availability"
                      />
                    )
                    : (
                      <Translation
                        id="widgets.unitsTablev2.storageMart.noLongerAvailable"
                        defaultMessage="No longer available, please call"
                      />
                    )
                  }
                </div>
                <Translation
                  id="sections.enterprise.storageMartHeader.phoneNumber"
                  defaultMessage="1234567890"
                >
                  {phoneNumber => (
                  <a
                    className="rnl-Button rnl-Button--neutral"
                    href={`tel:${phoneNumber}`}
                    data-marchex
                  >
                    {phoneNumber}
                  </a>
                  )}
                </Translation>
              </>
            )
            : (
              <>
                <ThemedButton
                  context="bare"
                  className={styles.reserveButton}
                  onClick={handleReserveUnit}
                  disabled={submitting}
                  loading={submitting}
                >
                  <Translation id="widgets.unitsTablev2.storageMart.reserveUnit" defaultMessage="Rent Now" />
                </ThemedButton>
              </>
            )}
        </div>
      </Col>
    </div>
  );
};

UnitListingRow.propTypes = propTypes;
UnitListingRow.defaultProps = defaultProps;

export default compose(
  connect(null, {
    completeReviewCost: reviewCost,
    dispatchClearReservation: clearReservation,
    dispatchClearMoveIn: clearMoveIn,
  }),
)(UnitListingRow);
